import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { API_BASE_URL } from "@/lib/api";

export type AdminExperienceItem = {
  id: string;
  partnerId: number;
  status: string;
  category: string;
  title: string;
  district: string;
  modifiedAt: string;
};

export type AdminExperienceDetail = {
  id: string;
  partnerId: number;
  status: string;
  category: string;
  basic?: {
    Title?: string;
    Description?: string;
    DurationHours?: number;
    MaxGroupSize?: number;
    Languages?: string[];
  };
  organizer?: {
    ProviderName?: string;
    ContactName?: string;
    ContactMobile?: string;
    ContactEmail?: string;
    BusinessType?: string;
    YearsExperience?: number;
  };
  location?: {
    District?: string;
    Address?: string;
    MeetingPoint?: string;
    TransportIncluded?: boolean;
    AccessibilityNotes?: string;
    Geo?: {
      Latitude?: number;
      Longitude?: number;
    };
  };
  pricing?: {
    PricePerPersonLkr?: number;
    MinBookingSize?: number;
    AvailableDays?: string;
    AdvanceBookingHours?: number;
    CancellationPolicyCode?: string;
  };
  compliance?: {
    InsuranceCoverage?: boolean;
    Licenses?: string[];
    RiskDisclosureConfirmed?: boolean;
    TermsAccepted?: boolean;
  };
  categoryFields?: {
    minAge?: number;
    difficulty?: string;
    fitnessLevel?: string;
    safetyMeasures?: string[];
    [key: string]: any;
  };
  media?: {
    id: string;
    type: string;
    publicUrl: string;
    sortOrder: number;
    isActive: boolean;
    createdAt: string;
  }[];
};

type FetchAdminExperiencesParams = {
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
};

type ApproveExperienceParams = {
  experienceId: string;
  adminUserId?: string;
  notes: string;
};

type RejectExperienceParams = {
  experienceId: string;
  adminUserId?: string;
};

type ExperienceAdminState = {
  loading: boolean;
  error: string | null;
  experiences: AdminExperienceItem[];
  currentPage: number;
  pageSize: number;
  totalCount: number;
  experienceDetail: AdminExperienceDetail | null;
  detailLoading: boolean;
  approvalLoading: boolean;
};

const initialState: ExperienceAdminState = {
  loading: false,
  error: null,
  experiences: [],
  currentPage: 1,
  pageSize: 10,
  totalCount: 0,
  experienceDetail: null,
  detailLoading: false,
  approvalLoading: false,
};

export const fetchAdminExperiences = createAsyncThunk(
  "experienceAdmin/fetchAdminExperiences",
  async (params: FetchAdminExperiencesParams, thunkApi) => {
    try {
      const query = new URLSearchParams();

      if (params.status) query.append("status", params.status);
      if (params.search) query.append("search", params.search);
      query.append("page", String(params.page ?? 1));
      query.append("pageSize", String(params.pageSize ?? 10));

      const response = await fetch(
        `${API_BASE_URL}/api/admin/experiences?${query.toString()}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json, text/plain, */*",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch experiences");
      }

      const data: AdminExperienceItem[] = await response.json();

      return {
        items: data ?? [],
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 10,
        totalCount: data?.length ?? 0,
      };
    } catch (error: any) {
      return thunkApi.rejectWithValue(
        error?.message || "Failed to fetch experiences",
      );
    }
  },
);

export const fetchAdminExperienceDetail = createAsyncThunk(
  "experienceAdmin/fetchAdminExperienceDetail",
  async (experienceId: string, thunkApi) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/experiences/${experienceId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json, text/plain, */*",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch experience detail");
      }

      const data: AdminExperienceDetail = await response.json();
      return data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(
        error?.message || "Failed to fetch experience detail",
      );
    }
  },
);

export const approveAdminExperience = createAsyncThunk(
  "experienceAdmin/approveAdminExperience",
  async (params: ApproveExperienceParams, thunkApi) => {
    try {
      const query = new URLSearchParams();
      if (params.adminUserId) query.append("adminUserId", params.adminUserId);

      const response = await fetch(
        `${API_BASE_URL}/api/admin/experiences/${params.experienceId}/approve${
          query.toString() ? `?${query.toString()}` : ""
        }`,
        {
          method: "POST",
          headers: {
            Accept: "application/json, text/plain, */*",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            notes: params.notes,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to approve experience");
      }

      return {
        experienceId: params.experienceId,
      };
    } catch (error: any) {
      return thunkApi.rejectWithValue(
        error?.message || "Failed to approve experience",
      );
    }
  },
);

export const rejectAdminExperience = createAsyncThunk(
  "experienceAdmin/rejectAdminExperience",
  async (params: RejectExperienceParams, thunkApi) => {
    try {
      const query = new URLSearchParams();
      if (params.adminUserId) query.append("adminUserId", params.adminUserId);

      const response = await fetch(
        `${API_BASE_URL}/api/admin/experiences/${params.experienceId}/reject${
          query.toString() ? `?${query.toString()}` : ""
        }`,
        {
          method: "POST",
          headers: {
            Accept: "application/json, text/plain, */*",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to reject experience");
      }

      return {
        experienceId: params.experienceId,
      };
    } catch (error: any) {
      return thunkApi.rejectWithValue(
        error?.message || "Failed to reject experience",
      );
    }
  },
);

const experienceAdminSlice = createSlice({
  name: "experienceAdmin",
  initialState,
  reducers: {
    clearAdminExperienceDetail: (state) => {
      state.experienceDetail = null;
      state.detailLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminExperiences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminExperiences.fulfilled, (state, action) => {
        state.loading = false;
        state.experiences = action.payload.items;
        state.currentPage = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(
        fetchAdminExperiences.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload || "Something went wrong";
        },
      )
      .addCase(fetchAdminExperienceDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminExperienceDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.experienceDetail = action.payload;
      })
      .addCase(
        fetchAdminExperienceDetail.rejected,
        (state, action: PayloadAction<any>) => {
          state.detailLoading = false;
          state.error = action.payload || "Something went wrong";
        },
      )
      .addCase(approveAdminExperience.pending, (state) => {
        state.approvalLoading = true;
        state.error = null;
      })
      .addCase(approveAdminExperience.fulfilled, (state) => {
        state.approvalLoading = false;
        if (state.experienceDetail) {
          state.experienceDetail.status = "APPROVED";
        }
      })
      .addCase(
        approveAdminExperience.rejected,
        (state, action: PayloadAction<any>) => {
          state.approvalLoading = false;
          state.error = action.payload || "Failed to approve experience";
        },
      )
      .addCase(rejectAdminExperience.pending, (state) => {
        state.approvalLoading = true;
        state.error = null;
      })
      .addCase(rejectAdminExperience.fulfilled, (state) => {
        state.approvalLoading = false;
        if (state.experienceDetail) {
          state.experienceDetail.status = "REJECTED";
        }
      })
      .addCase(
        rejectAdminExperience.rejected,
        (state, action: PayloadAction<any>) => {
          state.approvalLoading = false;
          state.error = action.payload || "Failed to reject experience";
        },
      );
  },
});

export const { clearAdminExperienceDetail } = experienceAdminSlice.actions;

export default experienceAdminSlice.reducer;